import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { PICFLOW_CONFIG } from '../picflow-config';

type JsonLDType = 'page' | 'global' | 'breadcrumb' | 'faq' | 'from-comp' | 'to-comp';

@Injectable({
  providedIn: 'root',
})
export class JsonLDService {
  private document = inject(DOCUMENT);
  private config = inject(PICFLOW_CONFIG);

  public get(type: JsonLDType): Params | null {
    const script = this.document.getElementById(`json-ld-${type}`);
    if (!script) {
      return null;
    }

    try {
      return JSON.parse(script.textContent || '{}') as Params;
    } catch (e) {
      console.error(`Failed to parse JSON-LD for type "${type}":`, e);
      return null;
    }
  }

  /**
   * Adds a JSON-LD script tag to the document head.
   * @param data
   * @return an identifier for the script that can be used to remove it later.
   */
  public add(type: JsonLDType, data: Params): string {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `json-ld-${type}`;
    script.textContent = JSON.stringify(data, null, 2);

    const existingScript = this.document.getElementById(script.id);
    if (existingScript) {
      existingScript.remove();
    }

    this.document.head.appendChild(script);
    return script.id;
  }

  public remove(type: JsonLDType): void {
    const script = this.document.getElementById(`json-ld-${type}`);
    if (script) {
      script.remove();
    }
  }

  constructor() {
    if (this.config.isTenantDomain) return;
    // Global schema
    this.add('global', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'https://www.picflow.com',
      name: 'Picflow',
      url: 'https://www.picflow.com',
      logo: '/assets/picflow-logo-400.png',
      sameAs: [
        'https://twitter.com/PicflowHQ/',
        'https://instagram.com/picflow/',
        'https://youtube.com/@Picflow',
        'https://linkedin.com/company/picflow/',
        'https://facebook.com/picflow/',
        'https://crunchbase.com/organization/picflow',
        'https://wikidata.org/wiki/Q124683139',
        'https://apps.apple.com/app/picflow/id6741537409',
        'https://play.google.com/store/apps/details?id=com.picflow.pwa.twa',
      ],
      legalName: 'Nusign AG',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'help@picflow.com',
          url: 'https://help.picflow.com',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingCount: '80',
        bestRating: '5',
        ratingValue: '4.9',
        worstRating: '4',
      },
    });
  }
}
